// Copyright (c) StrangeBee 2024

import { CopyValue } from '@Atoms/CopyValue/CopyValue';
import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';
import { handleNotificationError } from '@Helpers/notification';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import React, { ReactElement, useState } from 'react';

import './APIKeyButton.scss';

interface APIKeyButtonProps {
    onGet: (userId: string) => Promise<string>;
    onRevoke: (userId: string) => Promise<void>;
    onRenew: (userId: string) => Promise<string | void>;
    userId: string;
    hasKey: boolean;
    className?: string;
}

export const APIKeyButton = ({
    userId,
    hasKey,
    onGet,
    onRenew,
    onRevoke,
    className = '',
}: APIKeyButtonProps): ReactElement => {
    const i = useLocalMessage();
    const [key, setKey] = useState('');

    const onGetHandler = async () => {
        try {
            const key = await onGet(userId);
            setKey(key);
        } catch (e) {
            handleNotificationError(e);
        }
    };

    const onRenewHandler = async () => {
        try {
            const key = await onRenew(userId);

            if (!key) {
                return;
            }

            setKey(key);
        } catch (e) {
            handleNotificationError(e);
        }
    };

    const onRevokeHandler = async () => {
        await onRevoke(userId);
        setKey('');
    };

    return (
        <div className={classList('api-key-button', className)}>
            <CopyValue value={key} disabled={!key} block>
                <THInput
                    value={hasKey ? key || 'api-key-placeholder-secret' : ''}
                    type={!key ? 'password' : undefined}
                    onChange={(event) => event}
                    disabled={!hasKey}
                    autoComplete="off"
                />
            </CopyValue>
            <div className="api-key-button__section">
                <THButton onClick={onRenewHandler}>
                    {hasKey ? i('user.api-key.renew.label') : i('user.api-key.create.label')}
                </THButton>

                <THButton type="primary" onClick={onGetHandler} disabled={!hasKey}>
                    {i('user.api-key.reveal.label')}
                </THButton>

                <THButton color="red" type="primary" onClick={onRevokeHandler} disabled={!hasKey}>
                    {i('user.api-key.revoke.label')}
                </THButton>
            </div>
        </div>
    );
};
